import { useState } from "react";
import {
  useGetPrePumpCandidatesQuery,
  useScanPrePumpNowMutation,
  useManualPrePumpBuyMutation,
  useDeepResearchMutation,
} from "@/store/api";
import { DeepResearchDialog } from "./DeepResearchDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

/**
 * PrePumpCard — surfaces symbols matching the pre-pump signatures
 * derived from forensic study of BNB & FET past pumps.
 *
 * Pattern A — OVERSOLD_BOUNCE: RSI<25, deep 7d drawdown, far below
 * SMA20, mild vol expansion. The "FET-Feb-06" signature.
 *
 * Pattern B — MOMENTUM_CONTINUATION: RSI 40-72, drift 0-40%, near/
 * above SMA20, vol expansion ≥1.1x. The "BNB-May-30" signature.
 *
 * Per-row controls:
 *   Buy [size]   — operator-initiated buy bypassing governor pause,
 *                  uses same SL/TP/OCO as auto-trader (default $100)
 *   Chart ↗      — opens TradingView for the symbol in a new tab (1D)
 */
export function PrePumpCard() {
  // Score tiers calibrated to the actual scanner output. The auto-trader
  // fires at >=95 (rare), watchlist promotes at >=70 (uncommon), >=60
  // = meaningful pattern with vol confirmation, default. Below 60 is
  // mostly "pattern signature detected, trend doesn't confirm" — fine to
  // hide unless the operator wants to debug.
  const [minScore, setMinScore] = useState<0 | 60 | 80 | 95>(60);
  const cands = useGetPrePumpCandidatesQuery(
    { limit: 15, min_score: minScore },
    { pollingInterval: 60000 },
  );
  const [scanNow, { isLoading: scanning }] = useScanPrePumpNowMutation();
  const [manualBuy] = useManualPrePumpBuyMutation();
  const [deepResearch] = useDeepResearchMutation();

  // Per-symbol size input (default $100). Keyed by symbol so each row
  // has its own input. Buying state tracked per-symbol too.
  const [sizeBySymbol, setSizeBySymbol] = useState<Record<string, string>>({});
  const [buyingSymbol, setBuyingSymbol] = useState<string | null>(null);
  const [researchSymbol, setResearchSymbol] = useState<string | null>(null);
  const [researchReport, setResearchReport] = useState<any | null>(null);
  const [researchLoading, setResearchLoading] = useState(false);

  async function onResearch(symbol: string) {
    setResearchSymbol(symbol);
    setResearchReport(null);
    setResearchLoading(true);
    try {
      const r: any = await deepResearch({ symbol, use_cache: true }).unwrap();
      setResearchReport(r);
    } catch (e: any) {
      toast.error(`research failed: ${e?.data?.detail ?? e?.message ?? "?"}`);
      setResearchReport({ status: "error", reason: e?.data?.detail ?? "?" });
    } finally {
      setResearchLoading(false);
    }
  }

  async function onScan() {
    try {
      const r: any = await scanNow().unwrap();
      toast.success(`scan complete — ${r?.matches ?? 0} matches stored`);
      // Force-refetch the candidates query so user sees fresh data
      // immediately (default polling is every 2 min, too slow)
      await cands.refetch();
    } catch (e: any) {
      toast.error(`scan failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onBuy(symbol: string) {
    const sizeRaw = sizeBySymbol[symbol] ?? "100";
    const size = Number(sizeRaw);
    if (!Number.isFinite(size) || size <= 0) {
      toast.error("Enter a valid USDT size");
      return;
    }
    if (!confirm(`Buy $${size.toFixed(2)} of ${symbol}? Bypasses governor.`)) return;
    setBuyingSymbol(symbol);
    try {
      const r: any = await manualBuy({ symbol, size_usd: size }).unwrap();
      if (r?.status === "ok") {
        toast.success(
          `${symbol} BOUGHT $${r.size_usd.toFixed(2)} qty ${r.filled_qty} @ $${r.avg_price} — stop $${r.stop_price.toFixed(6)} target $${r.target_price.toFixed(6)}`,
          { duration: 8000 }
        );
      } else {
        toast.error(`${symbol} ${r?.status}: ${r?.reason ?? "?"}`);
      }
    } catch (e: any) {
      toast.error(`buy failed: ${e?.data?.detail ?? e?.message ?? "?"}`);
    } finally {
      setBuyingSymbol(null);
    }
  }

  // Open TradingView chart in a new tab. Binance symbols formatted as
  // BINANCE:BASEQUOTE (e.g. BINANCE:MEMEUSDT). Default 1D timeframe.
  function chartUrl(symbol: string): string {
    const compact = symbol.replace("/", "");
    return `https://www.tradingview.com/chart/?symbol=BINANCE%3A${compact}&interval=1D`;
  }

  if (cands.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>Pre-pump candidates</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    );
  }

  const minScoreLabel: Record<number, string> = {
    0: "All",
    60: "≥60 (meaningful)",
    80: "≥80 (high conviction)",
    95: "≥95 (fire threshold)",
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>Pre-pump candidates</span>
          <span className="text-xs text-muted font-normal">
            true pre-pump setups: Quiet Accumulation (BNB-style) + Capitulation Reversal (FET-style)
          </span>
        </CardTitle>
        <div className="flex items-center gap-1 flex-wrap">
          <div className="inline-flex border border-border rounded overflow-hidden text-xs">
            {([95, 80, 60, 0] as const).map(s => (
              <button
                key={s}
                onClick={() => setMinScore(s)}
                className={`px-2 py-1 ${minScore === s ? "bg-accent text-white" : "bg-bg2 hover:bg-bg2/80"}`}
                title={`Show candidates with pattern_score ${s === 0 ? "of any value" : `≥${s}`}`}
              >
                {minScoreLabel[s]}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={onScan} disabled={scanning}>
            {scanning ? "Scanning…" : "Scan now"}
          </Button>
        </div>
      </CardHeader>
      {(!cands.data || cands.data.length === 0) && (
        <CardContent>
          <div className="text-sm text-muted">
            No candidates with score {minScore === 0 ? "" : `≥${minScore}`} in the last 2h.{" "}
            {minScore > 0 && (
              <button
                className="underline hover:text-fg"
                onClick={() => setMinScore(0)}
              >
                Show all scores
              </button>
            )}
          </div>
        </CardContent>
      )}
      {cands.data && cands.data.length > 0 && (
      <>
      {/* keep the rest of the original rendering inside this fragment */}
      <CardContent>
        <Table>
          <THead>
            <TR>
              <TH>Symbol</TH>
              <TH className="text-right">Score</TH>
              <TH>Pattern</TH>
              <TH className="text-right">24h%</TH>
              <TH className="text-right">RSI</TH>
              <TH className="text-right">7d drift</TH>
              <TH className="text-right">vs SMA20</TH>
              <TH className="text-right">Vol×</TH>
              <TH className="text-center">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {cands.data.map((r) => {
              const tone = r.pattern_score >= 80 ? "good"
                         : r.pattern_score >= 60 ? "warn"
                                                   : "muted";
              const patternLabel = r.pattern_match.startsWith("A")
                ? "Quiet accumulation"
                : r.pattern_match.startsWith("B")
                ? "Capitulation reversal"
                : "Mixed";
              const patternTone = r.pattern_match.startsWith("A") ? "info" : "default";
              const isBuying = buyingSymbol === r.symbol;
              return (
                <TR key={r.id}>
                  <TD className="font-mono font-semibold">{r.symbol}</TD>
                  <TD className="text-right">
                    <Badge tone={tone}>{r.pattern_score.toFixed(0)}</Badge>
                  </TD>
                  <TD>
                    <Badge tone={patternTone}>{patternLabel}</Badge>
                  </TD>
                  <TD className={`text-right tabular-nums ${
                    r.change_24h_pct >= 0 ? "text-green-500" : "text-red-500"
                  }`}>
                    {r.change_24h_pct >= 0 ? "+" : ""}{r.change_24h_pct.toFixed(1)}%
                  </TD>
                  <TD className="text-right tabular-nums">{r.rsi_daily.toFixed(0)}</TD>
                  <TD className={`text-right tabular-nums ${
                    r.drift_7d_pct >= 0 ? "text-green-500" : "text-red-500"
                  }`}>
                    {r.drift_7d_pct >= 0 ? "+" : ""}{r.drift_7d_pct.toFixed(1)}%
                  </TD>
                  <TD className={`text-right tabular-nums ${
                    r.dist_from_sma20_pct >= 0 ? "text-green-500" : "text-red-500"
                  }`}>
                    {r.dist_from_sma20_pct >= 0 ? "+" : ""}{r.dist_from_sma20_pct.toFixed(1)}%
                  </TD>
                  <TD className={`text-right tabular-nums ${
                    r.vol_expansion_ratio >= 1.5 ? "text-green-500 font-semibold" : ""
                  }`}>
                    {r.vol_expansion_ratio.toFixed(2)}x
                  </TD>
                  <TD className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <input
                        type="number"
                        min="5"
                        max="1000"
                        step="5"
                        value={sizeBySymbol[r.symbol] ?? "100"}
                        onChange={(e) =>
                          setSizeBySymbol({ ...sizeBySymbol, [r.symbol]: e.target.value })
                        }
                        className="w-16 px-1 py-0.5 text-xs text-right rounded border bg-bg text-fg tabular-nums"
                        title="USDT size"
                        disabled={isBuying}
                      />
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onBuy(r.symbol)}
                        disabled={isBuying}
                        title="Manual buy — bypasses governor, uses auto-trader SL/TP/OCO"
                      >
                        {isBuying ? "Buying…" : "Buy"}
                      </Button>
                      <a
                        href={chartUrl(r.symbol)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open TradingView chart (1D)"
                      >
                        <Button size="sm" variant="ghost">Chart ↗</Button>
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onResearch(r.symbol)}
                        title="Deep 10-step research — multi-TF, analogs, rel-strength, Gemini Pro verdict"
                      >
                        Research
                      </Button>
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </CardContent>
      </>
      )}

      <DeepResearchDialog
        symbol={researchSymbol ?? ""}
        open={researchSymbol !== null}
        onClose={() => { setResearchSymbol(null); setResearchReport(null); }}
        report={researchReport}
        loading={researchLoading}
      />
    </Card>
  );
}
